import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, LogOut, Info } from "lucide-react";

/**
 * Forced password rotation on first sign-in.
 * Shown when auth response indicates `must_change_password === true`.
 * Blocks navigation elsewhere until password is rotated. User can also sign out.
 */
export default function ChangePasswordPage({ forced = false }) {
  const { refresh, logout, user } = useAuth();
  const nav = useNavigate();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const strong = pw.length >= 8 && /[A-Za-z]/.test(pw) && /\d/.test(pw);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated · continuing to app");
      await refresh();
      nav("/app/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Password change failed");
    } finally { setBusy(false); }
  };

  const signOut = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6" data-testid="change-password-page">
      <div className="w-full max-w-md border border-border bg-card rounded-md shadow-brut-orange p-8 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <ShieldCheck size={20} />
          </div>
          <div>
            <div className="overline">Security</div>
            <div className="font-display text-2xl">Choose a new password</div>
          </div>
        </div>

        {forced && (
          <div className="border border-primary/40 bg-red-50/60 text-primary text-xs rounded-md p-3 flex items-start gap-2" data-testid="force-notice">
            <Info size={13} className="mt-0.5 shrink-0" />
            <div>
              This is your first sign-in with a temporary password. Please rotate it before continuing —
              the admin who created your account cannot see the new one.
            </div>
          </div>
        )}

        {user && (
          <div className="text-xs text-muted-foreground mono">
            Signed in as <span className="text-foreground font-semibold">{user.email}</span> · <span className="uppercase">{user.role}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <Label className="overline">Temporary / current password</Label>
            <Input data-testid="cur-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)}
              className="mt-1 rounded-md" required autoFocus placeholder="Password admin shared with you" />
          </label>
          <label className="block">
            <Label className="overline">New password</Label>
            <Input data-testid="new-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              className="mt-1 rounded-md" required minLength={8} placeholder="Min 8 characters · include a number" />
            <div className="mt-1 text-[10px] flex items-center gap-1.5">
              <span className={`inline-block h-1.5 w-16 rounded-full ${strong ? "bg-emerald-500" : "bg-amber-400"}`} />
              <span className={strong ? "text-emerald-700" : "text-amber-700"}>{strong ? "Strong" : "Add letters + digit + 8 chars"}</span>
            </div>
          </label>
          <label className="block">
            <Label className="overline">Confirm new password</Label>
            <Input data-testid="confirm-password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
              className="mt-1 rounded-md" required minLength={8} placeholder="Re-enter" />
            {pw2 && pw !== pw2 && <div className="text-[10px] text-primary mt-1">Passwords don't match</div>}
          </label>
          <div className="flex items-center justify-between gap-2 pt-2">
            <Button data-testid="signout-btn" type="button" variant="outline" onClick={signOut} className="rounded-md">
              <LogOut size={13} className="mr-1.5" /> Sign out
            </Button>
            <Button data-testid="change-submit" type="submit" disabled={busy || !cur || !pw || pw !== pw2}
              className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
              <KeyRound size={13} className="mr-1.5" /> {busy ? "Updating…" : "Set new password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
